import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// API base URL from environment variable or default
const API_URL = process.env.REACT_APP_API_URL || 'https://api.youmeditation.com';

/**
 * Decode JWT token to get payload
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Invalid token = expired
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return currentTime >= expirationTime;
}

/**
 * Get token from localStorage or sessionStorage
 */
function getStoredToken(): string | null {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

/**
 * Clear stored token and trigger logout
 */
function clearAuthAndLogout(): void {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');

  // Import authService dynamically to avoid circular dependency
  import('./auth').then(({ authService }) => {
    authService.logout();
  });
}

/**
 * Create configured axios instance
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor: Add auth token and validate expiration
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();

    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpired(token)) {
        console.warn('Token expired, logging out...');
        clearAuthAndLogout();

        // Reject the request
        return Promise.reject(new Error('Token expired'));
      }

      // Add token to request headers
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: Handle auth errors globally
 */
apiClient.interceptors.response.use(
  (response) => {
    // Pass through successful responses
    return response;
  },
  (error: AxiosError) => {
    // Handle authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('Authentication failed (401/403), logging out...');
      clearAuthAndLogout();

      // Redirect to home (there is no /login route — login is a modal shown by
      // ProtectedRoute / the header). Avoid a hard redirect loop if already on home.
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
export { isTokenExpired, decodeJWT };
